import { useEffect, useRef, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChevronDown,
  faChevronRight,
} from "@fortawesome/free-solid-svg-icons";
import { IBudgetExpense } from "dto/Budget/IBudgetExpense";
import budgetService from "api/budgetService";
import { formatMoney, formatMoneyPrecise } from "utils/formatMoney";
import { generateExpenseMetricPdf } from "utils/generateExpenseMetricPdf";
import SplitSlide, { IPreviewState } from "components/SplitSlide";
import PdfPreviewPane from "components/SplitSlide/PdfPreviewPane";
import styles from "./styles.module.scss";

// Wspólne klucze proporcji/zoomu dla layoutu metryk (model 1 = SplitSlide).
const LS_SPLIT_KEY = "metric_slide_split_v1";
const LS_ZOOM_KEY = "metric_slide_zoom_v1";

interface Props {
  expense: IBudgetExpense;
  /** Suma rozliczeń (z faktur) wyliczona w wierszu — jeśli brak, użyte SettledAmount. */
  settledAmount?: number;
  onClose: () => void;
}

const ExpenseMetricSlide = ({ expense, settledAmount, onClose }: Props) => {
  const [generating, setGenerating] = useState(true);
  const [pdfBlob, setPdfBlob] = useState<Blob | null>(null);
  const [pdfError, setPdfError] = useState<string | null>(null);
  // Sekcje list (Zaangażowania/Faktury/Umowy/Postępowania/Teczki) domyślnie zwinięte.
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({
    eng: true,
    inv: true,
    con: true,
    tender: true,
    invest: true,
  });
  const toggleSection = (key: string) =>
    setCollapsed((p) => ({ ...p, [key]: !p[key] }));

  const chapterSymbol = expense.ChapterSymbol ?? "";
  const divisionSymbol = chapterSymbol.slice(0, 3);
  const settled = settledAmount ?? expense.SettledAmount ?? 0;
  // Pozostało = Budżet zadania − Zaangażowane (kolumna 1 − kolumna 2).
  const remaining = (expense.PlannedAmount ?? 0) - (expense.EngagedAmount ?? 0);

  // Nazwy działu/rozdziału (do symbolu cyfrowego dokładamy etykietę).
  const { data: divisions = [] } = budgetService.useGetDivisions();
  const { data: chapters = [] } = budgetService.useGetChapters(
    expense.DivisionId,
  );
  const division = divisions.find((d) => d.Id === expense.DivisionId);
  const chapter = chapters.find((c) => c.Id === expense.ChapterId);
  const divisionDisplay =
    [division?.Symbol ?? divisionSymbol, division?.Name]
      .filter(Boolean)
      .join(" — ") || "—";
  const chapterDisplay =
    [chapter?.Symbol ?? chapterSymbol, chapter?.Name]
      .filter(Boolean)
      .join(" — ") || "—";

  // Pozycje wydatku — to samo źródło co panel prawy (drzewo Listy wydatków).
  const { data: engagements = [] } = budgetService.useGetEngagements(
    expense.Id,
  );
  const { data: invoices = [] } = budgetService.useGetInvoices(expense.Id);
  const realInvoices = invoices.filter((inv) => !!inv.Number);
  const { data: contracts = [] } = budgetService.useGetContracts(expense.Id);
  const { data: tenders = [] } = budgetService.useGetTenders(expense.Id);
  const { data: investmentCases = [] } = budgetService.useGetInvestmentCases(
    expense.Id,
  );

  // Rozbicie budżetu zadania na paragrafy: kwota planowana z quoty wydatku;
  // zaangażowane/rozliczone agregowane po ExpenseQuotaId z quot zaangażowań/faktur.
  const { data: paragraphs = [] } = budgetService.useGetParagraphs();
  const paraSymbol = (id: string) =>
    paragraphs.find((p) => p.Id === id)?.Symbol ?? "—";
  const sumByQuota = (
    items: { Quotas?: { ExpenseQuotaId: string; Amount: number }[] }[],
  ) => {
    const map: Record<string, number> = {};
    items.forEach((it) =>
      (it.Quotas ?? []).forEach((q) => {
        map[q.ExpenseQuotaId] = (map[q.ExpenseQuotaId] ?? 0) + (q.Amount ?? 0);
      }),
    );
    return map;
  };
  const engagedByQuota = sumByQuota(engagements);
  const settledByQuota = sumByQuota(realInvoices);
  const paragraphRows = (expense.Quotas ?? []).map((q) => {
    const planned = q.Amount ?? 0;
    const engagedP = engagedByQuota[q.Id] ?? 0;
    return {
      label: `§ ${paraSymbol(q.ParagraphId)}${
        q.ParagraphSuffix ? " " + q.ParagraphSuffix : ""
      }`,
      planned,
      engaged: engagedP,
      settled: settledByQuota[q.Id] ?? 0,
      remaining: planned - engagedP,
    };
  });

  // Model 1 (SplitSlide): podgląd po lewej jest ZAWSZE widoczny, więc metrykę
  // generujemy automatycznie po otwarciu (bez przycisku „PDF").
  const loadedRef = useRef(false);
  useEffect(() => {
    if (loadedRef.current) return;
    loadedRef.current = true;
    (async () => {
      try {
        const blob = await generateExpenseMetricPdf({
          title: expense.Title ?? "",
          divisionSymbol: division?.Symbol ?? divisionSymbol,
          divisionName: division?.Name ?? "",
          chapterSymbol: chapter?.Symbol ?? chapterSymbol,
          chapterName: chapter?.Name ?? "",
          plannedAmount: expense.PlannedAmount ?? 0,
          engagedAmount: expense.EngagedAmount ?? 0,
          settledAmount: settled,
          paragraphs: paragraphRows,
          engagements: engagements.map((e) => ({
            primary: e.Signature ?? "",
            secondary: e.Title ?? "",
            amount: e.Amount ?? 0,
            status: e.Status ?? "",
          })),
          invoices: realInvoices.map((inv) => ({
            primary: inv.Number ?? "",
            secondary: [inv.Sender, inv.Title].filter(Boolean).join(" · "),
            amount: inv.Amount ?? 0,
          })),
          contracts: contracts.map((con) => ({
            primary: con.Sender ?? "",
            secondary: con.Title ?? con.Subject ?? "",
            amount: con.Value ?? 0,
          })),
          tenders: tenders.map((t) => ({
            primary: t.Number ?? "",
            secondary: t.Subject ?? "",
            amount: t.Amount ?? 0,
            status: t.Status ?? "",
          })),
          investmentCases: investmentCases.map((ic) => ({
            primary: ic.Signature ?? "",
            secondary: ic.Title ?? "",
          })),
        });
        setPdfBlob(blob);
      } catch {
        setPdfError("Nie udało się przygotować podglądu metryki.");
      } finally {
        setGenerating(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const previewState: IPreviewState | null = generating
    ? { text: "Generowanie podglądu metryki…" }
    : pdfError
    ? { text: pdfError, isError: true }
    : !pdfBlob
    ? { text: "Brak podglądu metryki." }
    : null;

  return (
    <SplitSlide
      title={
        chapterSymbol
          ? `Metryka wydatku — rozdział ${chapterSymbol}`
          : "Metryka wydatku"
      }
      splitStorageKey={LS_SPLIT_KEY}
      devTodoKey="slide:expense-metric"
      onClose={onClose}
      previewState={previewState}
      preview={
        pdfBlob && (
          <PdfPreviewPane
            file={pdfBlob}
            title="Metryka wydatku"
            zoomStorageKey={LS_ZOOM_KEY}
          />
        )
      }
    >
      <div className={styles.body}>
        {/* Dane wydatku — sekcja zwijana */}
        <div
          className={styles.sectionTitle}
          onClick={() => toggleSection("info")}
        >
          <span>Dane wydatku</span>
          <FontAwesomeIcon
            className={styles.sectionChevron}
            icon={collapsed.info ? faChevronRight : faChevronDown}
          />
        </div>
        {!collapsed.info && (
          <div className={styles.metaCard}>
            <div className={styles.metaTitleRow}>
              <div className={styles.metaTitleLabel}>Tytuł</div>
              <div className={styles.metaTitleValue}>
                {expense.Title || "—"}
              </div>
            </div>
            <div className={styles.metaGrid}>
              <div className={styles.metaItem} style={{ gridColumn: "span 2" }}>
                <span className={styles.metaLabel}>Dział</span>
                <span className={styles.metaValue}>{divisionDisplay}</span>
              </div>
              <div className={styles.metaItem} style={{ gridColumn: "span 2" }}>
                <span className={styles.metaLabel}>Rozdział</span>
                <span className={styles.metaValue}>{chapterDisplay}</span>
              </div>
            </div>
          </div>
        )}

        {/* Budżet zadania i stan wykonania — tabela z rozbiciem na paragrafy */}
        <div
          className={styles.sectionTitle}
          onClick={() => toggleSection("budget")}
        >
          <span>Budżet zadania i stan wykonania</span>
          <FontAwesomeIcon
            className={styles.sectionChevron}
            icon={collapsed.budget ? faChevronRight : faChevronDown}
          />
        </div>
        {!collapsed.budget && (
          <table className={styles.budgetTable}>
            <thead>
              <tr>
                <th></th>
                <th>Budżet zadania</th>
                <th>Zaangażowane</th>
                <th>Rozliczone</th>
                <th>Pozostało</th>
              </tr>
            </thead>
            <tbody>
              <tr className={styles.budgetTotalRow}>
                <td>Razem</td>
                <td>{formatMoneyPrecise(expense.PlannedAmount ?? 0)}</td>
                <td>{formatMoneyPrecise(expense.EngagedAmount ?? 0)}</td>
                <td>{formatMoneyPrecise(settled)}</td>
                <td>{formatMoneyPrecise(remaining)}</td>
              </tr>
              {paragraphRows.map((r, i) => (
                <tr key={i}>
                  <td>{r.label}</td>
                  <td>{formatMoneyPrecise(r.planned)}</td>
                  <td>{formatMoneyPrecise(r.engaged)}</td>
                  <td>{formatMoneyPrecise(r.settled)}</td>
                  <td>{formatMoneyPrecise(r.remaining)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {/* Zaangażowania — lista tożsama z panelem prawym */}
        <div
          className={styles.sectionTitle}
          onClick={() => toggleSection("eng")}
        >
          <span>Zaangażowania</span>
          <FontAwesomeIcon
            className={styles.sectionChevron}
            icon={collapsed.eng ? faChevronRight : faChevronDown}
          />
        </div>
        {!collapsed.eng &&
          (engagements.length === 0 ? (
            <div className={styles.noItems}>Brak zaangażowań</div>
          ) : (
            <div className={styles.itemList}>
              {engagements.map((eng) => (
                <div key={eng.Id} className={styles.itemRow}>
                  <span className={styles.itemSig}>{eng.Signature}</span>
                  <span className={styles.itemTitle}>{eng.Title}</span>
                  <span className={styles.itemMoney}>
                    {formatMoney(eng.Amount)}
                  </span>
                  <span className={styles.itemStatus}>{eng.Status ?? ""}</span>
                </div>
              ))}
            </div>
          ))}

        {/* Faktury — lista tożsama z panelem prawym */}
        <div
          className={styles.sectionTitle}
          onClick={() => toggleSection("inv")}
        >
          <span>Faktury</span>
          <FontAwesomeIcon
            className={styles.sectionChevron}
            icon={collapsed.inv ? faChevronRight : faChevronDown}
          />
        </div>
        {!collapsed.inv &&
          (realInvoices.length === 0 ? (
            <div className={styles.noItems}>Brak faktur</div>
          ) : (
            <div className={styles.itemList}>
              {realInvoices.map((inv) => (
                <div key={inv.Id} className={styles.itemRow}>
                  <span className={styles.itemSig}>{inv.Number}</span>
                  <span className={styles.itemTitleDouble}>
                    <span className={styles.itemTitleLine1}>{inv.Sender}</span>
                    <span className={styles.itemTitleLine2}>{inv.Title}</span>
                  </span>
                  <span className={styles.itemMoney}>
                    {formatMoney(inv.Amount)}
                  </span>
                </div>
              ))}
            </div>
          ))}

        {/* Umowy — lista tożsama z panelem prawym */}
        <div
          className={styles.sectionTitle}
          onClick={() => toggleSection("con")}
        >
          <span>Umowy</span>
          <FontAwesomeIcon
            className={styles.sectionChevron}
            icon={collapsed.con ? faChevronRight : faChevronDown}
          />
        </div>
        {!collapsed.con &&
          (contracts.length === 0 ? (
            <div className={styles.noItems}>Brak umów</div>
          ) : (
            <div className={styles.itemList}>
              {contracts.map((con) => (
                <div key={con.Id} className={styles.itemRow}>
                  <span className={styles.itemTitleDouble}>
                    <span className={styles.itemTitleLine1}>{con.Sender}</span>
                    <span className={styles.itemTitleLine2}>
                      {con.Title ?? con.Subject}
                    </span>
                  </span>
                  <span className={styles.itemMoney}>
                    {formatMoney(con.Value)}
                  </span>
                </div>
              ))}
            </div>
          ))}

        {/* Postępowania przetargowe — lista tożsama z panelem prawym */}
        <div
          className={styles.sectionTitle}
          onClick={() => toggleSection("tender")}
        >
          <span>Postępowania przetargowe</span>
          <FontAwesomeIcon
            className={styles.sectionChevron}
            icon={collapsed.tender ? faChevronRight : faChevronDown}
          />
        </div>
        {!collapsed.tender &&
          (tenders.length === 0 ? (
            <div className={styles.noItems}>Brak postępowań przetargowych</div>
          ) : (
            <div className={styles.itemList}>
              {tenders.map((t) => (
                <div key={t.Id} className={styles.itemRow}>
                  <span className={styles.itemSig}>{t.Number}</span>
                  <span className={styles.itemTitle}>{t.Subject}</span>
                  <span className={styles.itemMoney}>
                    {formatMoney(t.Amount)}
                  </span>
                  <span className={styles.itemStatus}>{t.Status ?? ""}</span>
                </div>
              ))}
            </div>
          ))}

        {/* Teczki inwestycji — lista tożsama z panelem prawym */}
        <div
          className={styles.sectionTitle}
          onClick={() => toggleSection("invest")}
        >
          <span>Teczki inwestycji</span>
          <FontAwesomeIcon
            className={styles.sectionChevron}
            icon={collapsed.invest ? faChevronRight : faChevronDown}
          />
        </div>
        {!collapsed.invest &&
          (investmentCases.length === 0 ? (
            <div className={styles.noItems}>Brak teczek inwestycji</div>
          ) : (
            <div className={styles.itemList}>
              {investmentCases.map((ic) => (
                <div key={ic.Id} className={styles.itemRow}>
                  <span className={styles.itemSig}>{ic.Signature}</span>
                  <span className={styles.itemTitle}>{ic.Title}</span>
                </div>
              ))}
            </div>
          ))}
      </div>
    </SplitSlide>
  );
};

export default ExpenseMetricSlide;
